import { useEffect, useState, useCallback } from "react";
import api, { eur } from "@/lib/api";
import { PageHeader } from "@/components/common";
import { TrendingUp, FileText, Wrench, Trophy, Building2 } from "lucide-react";

const today = () => new Date().toISOString().slice(0, 10);
const monthStart = () => new Date().toISOString().slice(0, 7) + "-01";
const yearStart = () => new Date().getFullYear() + "-01-01";

const PRESETS = [
  ["month", "Tento mesiac"],
  ["year", "Tento rok"],
  ["all", "Všetko"],
  ["custom", "Vlastné"],
];

function Kpi({ icon: Icon, label, value, tone, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</span>
        <Icon className={`h-5 w-5 ${tone}`} />
      </div>
      <div className="mt-3 font-heading text-3xl font-extrabold">{value}</div>
    </div>
  );
}

function RankList({ title, icon: Icon, items, empty, renderMain, renderValue, maxValue, barColor, testid }) {
  return (
    <div className="card-surface p-5" data-testid={testid}>
      <h3 className="mb-4 flex items-center gap-2 font-heading text-lg font-bold uppercase"><Icon className="h-5 w-5 text-primary" /> {title}</h3>
      {items?.length ? (
        <div className="space-y-3">
          {items.map((it, i) => {
            const pct = maxValue > 0 ? Math.max(6, Math.round((it._bar / maxValue) * 100)) : 0;
            return (
              <div key={i} data-testid={`${testid}-row-${i}`}>
                <div className="mb-1 flex items-center justify-between gap-3 text-sm">
                  <span className="flex min-w-0 items-center gap-2">
                    <span className="grid h-5 w-5 shrink-0 place-items-center rounded bg-secondary text-[10px] font-bold">{i + 1}</span>
                    <span className="truncate font-medium">{renderMain(it)}</span>
                  </span>
                  <span className="shrink-0 font-mono text-muted-foreground">{renderValue(it)}</span>
                </div>
                <div className="h-1.5 w-full overflow-hidden rounded-full bg-background">
                  <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: barColor }} />
                </div>
              </div>
            );
          })}
        </div>
      ) : <p className="text-sm text-muted-foreground">{empty}</p>}
    </div>
  );
}

export default function Statistics() {
  const [preset, setPreset] = useState("year");
  const [start, setStart] = useState(yearStart());
  const [end, setEnd] = useState(today());
  const [data, setData] = useState(null);

  const applyPreset = (p) => {
    setPreset(p);
    if (p === "month") { setStart(monthStart()); setEnd(today()); }
    else if (p === "year") { setStart(yearStart()); setEnd(today()); }
    else if (p === "all") { setStart(""); setEnd(""); }
  };

  const load = useCallback(async () => {
    const { data } = await api.get("/stats", { params: { start, end } });
    setData(data);
  }, [start, end]);
  useEffect(() => { load(); }, [load]);

  const maxCust = Math.max(1, ...(data?.top_customers || []).map((c) => c.revenue));

  return (
    <div>
      <PageHeader title="Štatistiky" subtitle="Najlepší zákazníci podľa zákaziek (bez DPH)" />

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="flex gap-1 rounded-lg border border-border bg-card p-1">
          {PRESETS.map(([val, lbl]) => (
            <button key={val} onClick={() => applyPreset(val)} data-testid={`stats-preset-${val}`}
              className={`rounded-md px-3 py-2 text-sm font-medium transition-colors ${preset === val ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:text-foreground"}`}>{lbl}</button>
          ))}
        </div>
        {preset === "custom" && (
          <div className="flex items-center gap-2">
            <input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-3 text-sm" data-testid="stats-start" />
            <span className="text-muted-foreground">–</span>
            <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="h-10 rounded-lg border border-border bg-background px-3 text-sm" data-testid="stats-end" />
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Kpi icon={TrendingUp} label="Tržby za obdobie" value={data ? eur(data.totals.revenue) : "–"} tone="text-emerald-400" testid="stats-kpi-revenue" />
        <Kpi icon={FileText} label="Počet faktúr" value={data?.totals.invoices ?? "–"} tone="text-primary" testid="stats-kpi-invoices" />
        <Kpi icon={Wrench} label="Počet zákaziek" value={data?.totals.jobs ?? "–"} tone="text-blue-400" testid="stats-kpi-jobs" />
      </div>

      <div className="mt-6">
        <RankList
          title="Najlepší zákazníci (bez DPH)" icon={Trophy} testid="top-customers"
          items={(data?.top_customers || []).map((c) => ({ ...c, _bar: c.revenue }))}
          empty="Za toto obdobie nie sú žiadni zákazníci."
          maxValue={maxCust} barColor="#10b981"
          renderMain={(c) => (<span className="flex items-center gap-1.5"><Building2 className="h-3.5 w-3.5 text-muted-foreground" />{c.name}</span>)}
          renderValue={(c) => `${eur(c.revenue)} · ${c.jobs} zák.`}
        />
      </div>
    </div>
  );
}
